/**
 * JamLoop CTV-attribution pixel: one event per eligible arrival on the public
 * landing page, and nothing else.
 *
 * Called once from `index.tsx` before React mounts, so the `/` → `/chat`
 * redirect, remounts, Strict Mode and SPA navigation never re-evaluate it. The
 * SDK registers no navigation or interaction listeners; its single `track()`
 * is the only event.
 *
 * The SDK reports `location.href`, sets a first-party `__adcloudiq` cookie, and
 * loads Tapad, which independently reads the full URL, the referrer, and UA
 * client hints. None of that is configurable, so eligibility is strict: the
 * landing URL may carry nothing but `utm_*` tags.
 *
 * Advertising partners are opt-out: an eligible arrival loads the SDK unless
 * the voter has turned them off (Your Privacy Choices); turning them back on
 * later in the same page loads it then.
 */
import { getAuth0Domain, getJamloopPixelHost, isJamloopPixelEnabled } from '@/config/env';
import { isEmbed } from '@/config/embed';
import { isSyntheticSession } from '@/config/synthetic';
import { getCapturedResearchAddress } from './researchAddressBootstrap';
import { detectInAppBrowser } from './detectOs';
import { whenAdConsentGranted } from './adConsentStore';
import { isReportableUrl } from './adPartnerUrl';

const SDK_SRC = 'https://p.jmlp.app/sdk.js';
const ADVERTISER_ID = 'd7eced48-837e-4720-8a48-60c2f9fde2ca';
const PIXEL_ID = '20ab944e-1381-4d9e-bdc0-4e70bd9244df';
const LOADED_KEY = '__votemateJamloopPixel';

const ALLOWED_QUERY_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]);

export interface LandingArrival {
  enabled: boolean;
  allowedHost: string | undefined;
  href: string;
  referrer: string;
  navigationType: string | undefined;
  authDomain: string | undefined;
  userAgent: string;
  isFramed: boolean;
  isEmbed: boolean;
  isSynthetic: boolean;
  hasResearchAddress: boolean;
  globalPrivacyControl: boolean;
}

/** Native-app webviews and in-app browsers, which are out of scope for landing measurement. */
export const isEmbeddedWebView = (ua: string): boolean =>
  detectInAppBrowser(ua) !== null ||
  // Android System WebView marks itself with `; wv)`.
  /; wv\)/.test(ua) ||
  // WKWebView reports AppleWebKit without the `Safari/` token every Apple browser carries.
  (/AppleWebKit\//.test(ua) && !/Safari\//.test(ua));

const hostnameOf = (href: string): string | null => {
  try {
    return new URL(href).hostname;
  } catch {
    return null;
  }
};

export const isEligibleLandingArrival = (arrival: LandingArrival): boolean => {
  if (!arrival.enabled || !arrival.allowedHost) return false;

  const url = new URL(arrival.href);
  if (url.hostname !== arrival.allowedHost || url.pathname !== '/' || url.hash) return false;
  for (const key of url.searchParams.keys()) {
    if (!ALLOWED_QUERY_PARAMS.has(key)) return false;
  }

  if (arrival.navigationType === 'reload' || arrival.navigationType === 'back_forward') {
    return false;
  }
  // A full load of `/` from our own pages or the Auth0 logout hop is a return, not an arrival.
  const referrerHost = arrival.referrer ? hostnameOf(arrival.referrer) : null;
  if (referrerHost && (referrerHost === url.hostname || referrerHost === arrival.authDomain)) {
    return false;
  }

  return !(
    arrival.isFramed ||
    arrival.isEmbed ||
    arrival.isSynthetic ||
    arrival.hasResearchAddress ||
    arrival.globalPrivacyControl ||
    isEmbeddedWebView(arrival.userAgent)
  );
};

const readArrival = (): LandingArrival => {
  let isFramed = true;
  try {
    isFramed = window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which means we are framed.
  }
  const navigation = performance.getEntriesByType?.('navigation')[0] as
    PerformanceNavigationTiming | undefined;

  return {
    enabled: isJamloopPixelEnabled(),
    allowedHost: getJamloopPixelHost(),
    href: window.location.href,
    referrer: document.referrer,
    navigationType: navigation?.type,
    authDomain: getAuth0Domain()
      ?.replace(/^https?:\/\//, '')
      .replace(/\/.*$/, ''),
    userAgent: navigator.userAgent,
    isFramed,
    isEmbed: isEmbed(),
    isSynthetic: isSyntheticSession(),
    hasResearchAddress: getCapturedResearchAddress() !== null,
    globalPrivacyControl:
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true,
  };
};

type AdCloudIqQueue = { push: (callback: () => void) => unknown };
type AdCloudIqSdk = {
  track: (params: { advertiserId: string; pixelId: string; pageUrl: string }) => void;
};

// The vendor snippet as supplied: queue the track call, then load the SDK, which
// drains the queue. `pageUrl` (a documented SDK attribute) names the landing page
// being measured, since partners can be turned back on after the router has left it.
// The queue drains only once the SDK has downloaded, by which time the router may
// have moved on, so `canTrack` re-checks the live URL right before tracking.
const injectSdk = (landingHref: string, canTrack: () => boolean): void => {
  const w = window as unknown as { __adcloudiq__?: AdCloudIqQueue };
  w.__adcloudiq__ = w.__adcloudiq__ || [];
  w.__adcloudiq__.push(() => {
    try {
      if (!canTrack()) return;
      (w.__adcloudiq__ as unknown as AdCloudIqSdk).track({
        advertiserId: ADVERTISER_ID,
        pixelId: PIXEL_ID,
        pageUrl: landingHref,
      });
    } catch {
      // The SDK throws on validation errors; tracking never interrupts the app.
    }
  });

  const script = document.createElement('script');
  script.src = SDK_SRC;
  script.async = true;
  document.head.appendChild(script);
};

export const loadJamloopPixel = (): void => {
  try {
    const w = window as unknown as Record<string, unknown>;
    if (w[LOADED_KEY]) return;
    w[LOADED_KEY] = true;

    // Snapshot now: by the time a prerendered page is shown, the router may have left `/`.
    const arrival = readArrival();
    const reportable = () => isReportableUrl(window.location.href, arrival.href);
    const inject = () => {
      try {
        injectSdk(arrival.href, reportable);
      } catch {
        // Tracking never interrupts the app.
      }
    };
    const run = () => {
      try {
        if (!isEligibleLandingArrival(arrival)) return;
        // The SDK and Tapad read the live URL, which by the time consent arrives
        // (or a prerendered page is shown) may no longer be the landing URL, so
        // it is rechecked when the SDK would load.
        whenAdConsentGranted(inject, reportable);
      } catch {
        // Tracking never interrupts the app.
      }
    };

    // A prerendered page may never be opened, so wait until the visitor actually sees it.
    if ((document as Document & { prerendering?: boolean }).prerendering) {
      document.addEventListener('prerenderingchange', run, { once: true });
    } else {
      run();
    }
  } catch {
    // Tracking never interrupts the app.
  }
};
